/**
 * React Hook for Unified Tracking
 *
 * Convenience hook that provides tracking functions with automatic cleanup.
 * Use this in React components for easier access to tracking features.
 */

'use client';

import { useCallback, useEffect } from 'react';
import { trackEvent, identifyUser, trackPageView } from './client';
import type { TrackingEvent, TrackingIdentity } from './types';

/**
 * Hook for tracking events in React components
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { track, identify, trackPage } = useTracking();
 *
 *   useEffect(() => {
 *     trackPage({ section: 'hero' });
 *   }, [trackPage]);
 *
 *   const handleClick = () => {
 *     track({
 *       eventName: 'button_clicked',
 *       properties: { button: 'signup' },
 *     });
 *   };
 *
 *   return <button onClick={handleClick}>Click</button>;
 * }
 * ```
 */
export function useTracking() {
  /**
   * Track a custom event
   */
  const track = useCallback(async (event: TrackingEvent) => {
    return trackEvent(event);
  }, []);

  /**
   * Identify the current user
   */
  const identify = useCallback(async (identity: TrackingIdentity) => {
    return identifyUser(identity);
  }, []);

  /**
   * Track a page view
   */
  const trackPage = useCallback(
    async (properties?: Record<string, unknown>) => {
      return trackPageView(properties);
    },
    [],
  );

  return {
    track,
    identify,
    trackPage,
  };
}

/**
 * Hook for automatic page view tracking
 *
 * Automatically tracks a page view when component mounts.
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   usePageTracking({
 *     page_section: 'waitlist',
 *     page_type: 'landing',
 *   });
 *
 *   return <div>My Page</div>;
 * }
 * ```
 */
export function usePageTracking(properties?: Record<string, unknown>) {
  useEffect(() => {
    trackPageView(properties);
  }, [properties]);
}

/**
 * Hook for tracking element clicks
 *
 * Returns a click handler that automatically tracks the event.
 *
 * @example
 * ```tsx
 * function MyButton() {
 *   const handleClick = useClickTracking({
 *     eventName: 'button_clicked',
 *     properties: { button_name: 'Sign Up' },
 *   });
 *
 *   return <button onClick={handleClick}>Sign Up</button>;
 * }
 * ```
 */
export function useClickTracking(event: TrackingEvent) {
  return useCallback(
    async (e?: React.MouseEvent) => {
      // Don't prevent default - let normal click behavior happen
      await trackEvent(event);
    },
    [event],
  );
}

/**
 * Hook for tracking form field changes
 *
 * Useful for tracking when users enter email (for identification).
 *
 * @example
 * ```tsx
 * function EmailInput() {
 *   const [email, setEmail] = useState('');
 *   const handleEmailChange = useFormTracking();
 *
 *   const handleBlur = () => {
 *     if (email) {
 *       handleEmailChange('email_entered', { email });
 *     }
 *   };
 *
 *   return <input onBlur={handleBlur} />;
 * }
 * ```
 */
export function useFormTracking() {
  return useCallback(
    async (eventName: string, properties?: Record<string, unknown>) => {
      await trackEvent({
        eventName,
        properties,
      });
    },
    [],
  );
}
